import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { RefreshCcw, Clock } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { CreateUserDialog } from "./CreateUserDialog";

export function UserHeader({ tablet = false }: { tablet?: boolean }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isFetching = useIsFetching({ queryKey: ["admin", "users"] }) > 0;
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  const goToAuditLogs = () =>
    navigate({ to: "/admin/audit", search: { entityType: "user" } });

  if (tablet) {
    return (
      <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
        <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
          {t`All Users`}
        </h1>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" className="h-10 gap-2" onClick={refresh}>
            <RefreshCcw className={isFetching ? "animate-spin" : ""} />
            {t`Refresh`}
          </Button>
          <Button variant="outline" size="sm" className="h-10 gap-2" onClick={goToAuditLogs}>
            <Clock />
            {t`Audit Logs`}
          </Button>
          <CreateUserDialog />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-3">
      <div>
        <h1 className="text-5xl font-bold tracking-tight text-(--color-text-primary)">
          {t`All Users`}
        </h1>
        <p className="text-(--color-text-secondary) mt-1">
          {t`Search, sort, and filter users across all concepts.`}
        </p>
      </div>
      <div className="flex gap-3">
        <Button variant="ghost" onClick={refresh}>
          <RefreshCcw className={isFetching ? "animate-spin" : ""} />
          {t`Refresh`}
        </Button>
        <Button variant="outline" onClick={goToAuditLogs}>
          <Clock />
          {t`Audit Logs`}
        </Button>
        <CreateUserDialog />
      </div>
    </div>
  );
}
